import { MEDICAL_TEAM_PATH } from '../registry/doctorSeeds'
import { unreviewed } from '../registry/shared'
import { BrandEntry, DoctorEntry } from '../types'

/**
 * PREVIEW-ONLY data read from the Medleve platform (production database, read-only) on 2026-10-06.
 * It feeds the review-only template previews under `/medleve-preview/seo` and NOTHING ELSE: it is not part
 * of the registry, the sitemap, internal links or any public page. Moving any of it into the registry
 * requires the usual review (see the notes on each block).
 */
export const PLATFORM_DATA_SNAPSHOT_DATE = '2026-10-06'

type DoctorSeed = Omit<DoctorEntry, 'kind' | 'path' | 'published' | 'requiresReview' | 'review'>

/**
 * Physicians with at least 100 Medleve consultations in the 90 days before the snapshot, most active first.
 * Fields are the platform's own, verbatim: name, CRM as registered (number + UF), profile photo URL and
 * the lines of the doctor's public profile text (shown under "Formação" although some lines describe
 * experience). The platform has NO RQE and no structured specialty (every record says only "Médico").
 * `honorificPrefix` is set only when the doctor's own profile text uses a gendered form; never guessed.
 *
 * REQUIRES LEGAL REVIEW — publishing a doctor's name, CRM, photo and profile text needs each doctor's
 * consent and a check of the text (e.g. one profile reads "Graduanda em Medicina").
 */
const PLATFORM_DOCTOR_SEEDS: DoctorSeed[] = [
  {
    slug: 'fernanda-ferreira-souza',
    name: 'Fernanda Ferreira Souza',
    honorificPrefix: 'Dra.',
    crm: { number: '52-0112103-0', uf: 'RJ' },
    image: 'https://storage.googleapis.com/doutores-omens/3x4%20-%20Fernanda.jpeg',
    education: [
      'Graduada em Medicina - Universidade Federal do Estado do Rio de Janeiro',
      'Residência em Medicina de Família e Comunidade - SMS Rio de Janeiro',
      'Residência em Administração em Saúde - UERJ',
      'Residente em Radiologia e Diagnóstico por imagem na UFRJ',
      'Trabalho para a Clínica Médica Dom Pedro e para Clube.'
    ]
  },
  {
    slug: 'caio-ariero',
    name: 'Caio Ariero',
    honorificPrefix: 'Dr.',
    crm: { number: '226953', uf: 'SP' },
    image: 'https://storage.googleapis.com/doutores-omens/WhatsApp%20Image%202025-06-25%20at%2017.10.52%20(1).jpeg',
    education: [
      'Graduado em Medicina pela Universidade de São Paulo',
      'Internato – Hospital das Clínicas da Faculdade de Medicina da USP',
      'Mais de 3000 atendimentos pela Plataforma',
      'Suporte Avançado de Vida Cardiovascular'
    ]
  },
  {
    slug: 'danilo-hojo-navarro',
    name: 'Danilo Hojo Navarro',
    honorificPrefix: 'Dr.',
    crm: { number: '176824', uf: 'SP' },
    image: 'https://storage.googleapis.com/doutores-omens/danilo-hojo-navarro-foto.jpg',
    education: [
      'Graduado na Faculdade de Medicina da Irmandade da Santa Casa de São Paulo.',
      'Residência em Medicina de Família pela Irmandade da Santa Casa de São Paulo. Especialista em Preceptoria em Medicina de Família pelo UNA-SUS.',
      'Capacitação em Lifestyle Medicine pela Universidade de Harvard (EUA).',
      'Corpo clínico do Hospital Sírio Libanês desde 2018 até dia atual.',
      'Preceptor em APS e medicina de Família pela faculdade Israelita Albert Einstein em 2018.'
    ]
  },
  {
    slug: 'lorraine-pacheco-carlos',
    name: 'Lorraine Pacheco Carlos',
    honorificPrefix: 'Dra.',
    crm: { number: '52-1373302', uf: 'RJ' },
    image: 'https://storage.googleapis.com/doutores-omens/Foto%203x4%20-%20Lorraine.jpeg',
    education: [
      'Graduanda em Medicina',
      'Cursando Pós em Medicina de Família e Comunidade',
      'Médica na Clínica César Pernetta - Rio de Janeiro'
    ]
  },
  {
    slug: 'bruno-lopes-bridi',
    name: 'Bruno Lopes Bridi',
    honorificPrefix: 'Dr.',
    crm: { number: '259091', uf: 'SP' },
    image: 'https://storage.googleapis.com/doutores-omens/foto-bruno-lopes-bridi.jpg',
    education: ['Graduado em Medicina pela Faculdade Israelita de Ciências da Saúde Albert Einstein (FICSAE).']
  },
  {
    slug: 'daniel-dos-santos-andrade',
    name: 'Daniel dos Santos Andrade',
    crm: { number: '521143239', uf: 'RJ' },
    image: 'https://storage.googleapis.com/doutores-omens/Foto%203x4%20Daniel.jpg',
    education: [
      'Advanced CardioVascular Life Support (CUREM) 2019, 2021 e 2024',
      'Pós graduação em Saúde Coletiva pela Universidade Federal da Bahia (UFBA) 374h/ 2020 - 2021',
      'Graduação em Medicina pela Universidade Federal do Estado do RJ (UNIRIO) 2012-2019',
      'Bacharelado Sanduíche pela London South Bank University (LSBU) 12 meses/ 2014-2015',
      'Internista do Hospital Municipal Raul Sertã em Nova Friburgo - RJ e como clínico no grupo SEGUMED de clínicas de hemodiálise no Rio de Janeiro - RJ'
    ]
  },
  {
    slug: 'erica-soares-de-oliveira',
    name: 'Érica Soares de Oliveira',
    honorificPrefix: 'Dra.',
    crm: { number: '51824', uf: 'BA' },
    image: 'https://storage.googleapis.com/doutores-omens/E%CC%81rica%20-%20Foto%203x4.png',
    education: [
      'Graduada em Medicina na UNIRIO - Universidade Federal do Estado do Rio de Janeiro',
      'Residência em Medicina de Família e Comunidade na UFOP - Universidade Federal de Ouro Preto',
      'MFC da Prefeitura de Ouro Preto'
    ]
  },
  {
    slug: 'eduarda-ribeiro-sanches',
    name: 'Eduarda Ribeiro Sanches',
    honorificPrefix: 'Dra.',
    crm: { number: '52-135646-1', uf: 'RJ' },
    image: 'https://storage.googleapis.com/doutores-omens/Foto%203x4%20Eduarda.png',
    education: [
      'Graduada na Universidade Estácio de Sá, Angra dos Reis.',
      'Pós-graduanda em Saúde do Idoso pela UNICV.',
      'Plantonista na Unidade de Pronto Atendimento do Hospital Municipal da Japuíba, Hospital da Unimed.'
    ]
  },
  {
    slug: 'leonardo-costa-soares',
    name: 'Leonardo Costa Soares',
    honorificPrefix: 'Dr.',
    crm: { number: '267456', uf: 'SP' },
    image: 'https://storage.googleapis.com/doutores-omens/foto%20leonardo.jpeg',
    education: [
      'Graduacao em medicina pela Universidade Sao Francisco.',
      'Pos-graduado em medicina endocanabinoide pela Wecann Academy.',
      'Experiencia em medicina da familia e comunidade.'
    ]
  },
  {
    slug: 'ornella-maria-covella-resende',
    name: 'Ornella Maria Covella Resende',
    honorificPrefix: 'Dra.',
    crm: { number: '245328', uf: 'SP' },
    image: 'https://storage.googleapis.com/doutores-omens/Foto-3x4-ornella.jpg',
    education: [
      'Graduada na Universidade cidade de São Paulo',
      'Pós-graduanda em Psiquiatria pela Beneficência Portuguesa',
      'Corpo clínico do Hospital NEXT (São Bernardo do Campo) e do Hospital Salvalus (São Paulo)',
      'Consultas particulares (generalista)'
    ]
  },
  {
    slug: 'pedro-henrique-alves-da-costa',
    name: 'Pedro Henrique Alves da Costa',
    honorificPrefix: 'Dr.',
    crm: { number: '204593', uf: 'SP' },
    image: 'https://storage.googleapis.com/doutores-omens/FOTO%203X4_page-0001.jpg',
    education: [
      'Graduado pela Universidade Federal do Tocantins em 2015',
      'Residência Médica em Clínica Médica finalizada em 2019',
      'Atuação no corpo clínico do Hospital Santa Helena São Bernardo do Campo e Hospital Cruz Azul'
    ]
  },
  {
    slug: 'nelson-alves-da-silva-junior',
    name: 'Nelson Alves da Silva Junior',
    crm: { number: '191458', uf: 'SP' },
    image: 'https://storage.googleapis.com/doutores-omens/3x4%20-%20Nelson.jpeg',
    education: [
      'Graduação em Medicina - UNIGRANRIO',
      'Residência em Saúde da Família e Comunidade - Secretaria municipal de Saúde do Rio de Janeiro',
      'Gestão de Saúde - IBCMED',
      'Pós-graduação no nível Especialização'
    ]
  },
  {
    slug: 'maria-eduarda-da-silva-muniz',
    name: 'Maria Eduarda da Silva Muniz',
    honorificPrefix: 'Dra.',
    crm: { number: '273895', uf: 'SP' },
    image: 'https://storage.googleapis.com/doutores-omens/Maria%20Eduarda%203x4.jpg',
    education: [
      'Médica formada pela Faculdade Israelita Albert Einstein',
      'Corpo Clínico do Hospital M Boi Mirim e Ama Pirajussara, administrados pelo Hospital Albert Einstein',
      'Corpo Clínico da UPA Jardim Helena.'
    ]
  },
  {
    slug: 'luiza-rodrigues-de-souza',
    name: 'Luiza Rodrigues de Souza',
    honorificPrefix: 'Dra.',
    crm: { number: '262730', uf: 'SP' },
    image: 'https://storage.googleapis.com/doutores-omens/foto-luiza-rodrigues-de-souza.jpg',
    education: [
      'Médica formada pela Faculdade Israelita Albert Einstein.',
      'Médica clínica na Medicina Ocupacional do Hospital de Urgência de São Bernardo - Fundação ABC.'
    ]
  }
]

export const PLATFORM_DOCTORS: DoctorEntry[] = PLATFORM_DOCTOR_SEEDS.map((seed) => ({
  ...seed,
  kind: 'doctor',
  path: `${MEDICAL_TEAM_PATH}/${seed.slug}`,
  published: false,
  requiresReview: false,
  review: unreviewed()
}))

const formatBrl = (value: number): string =>
  `R$ ${value
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`

const priceList = (items: Array<{ dose: string; price: number }>): string =>
  items.map(({ dose, price }) => `${dose}: ${formatBrl(price)}`).join('; ')

/**
 * What the platform catalog holds for the PRD brands. Only Mounjaro and Wegovy are in the catalog;
 * Ozempic, Poviztra, Semavy and Ozivy have no product, so they get nothing here.
 * Prices are the partner-pharmacy list prices per box on the snapshot date (promotional variants left out).
 *
 * REQUIRES MEDICAL REVIEW (doses, frequency) and REQUIRES LEGAL REVIEW (price, PRD §6: the sale is made by
 * the partner pharmacy, not by Medleve). Prices change: never copy these numbers into a public page
 * without a live source and a review date (PRD §57).
 */
export const PLATFORM_BRAND_DATA: Record<string, Partial<BrandEntry>> = {
  mounjaro: {
    manufacturer: 'Lilly',
    availableDoses: ['2,5 mg', '5 mg', '7,5 mg', '10 mg', '12,5 mg', '15 mg'],
    administrationFrequency: 'Aplicação subcutânea 1 vez por semana (conforme cadastrado no catálogo da plataforma).',
    priceInformation: `Preço por caixa na farmácia parceira disponível na plataforma em 06/10/2026 — ${priceList([
      { dose: '2,5 mg', price: 1422.52 },
      { dose: '5 mg', price: 1779.43 },
      { dose: '7,5 mg', price: 2299 },
      { dose: '10 mg', price: 2699 },
      { dose: '12,5 mg', price: 3199 },
      { dose: '15 mg', price: 3499 }
    ])}. O medicamento também pode ser adquirido na farmácia de preferência do paciente.`
  },
  wegovy: {
    availableDoses: ['0,25 mg', '0,5 mg', '1 mg', '1,7 mg', '2,4 mg'],
    administrationFrequency: 'Aplicação 1 vez por semana (conforme cadastrado no catálogo da plataforma).',
    priceInformation: `Preço por caixa na farmácia parceira disponível na plataforma em 06/10/2026 — ${priceList([
      { dose: '0,25 mg', price: 875 },
      { dose: '0,5 mg', price: 965 },
      { dose: '1 mg', price: 999 },
      { dose: '1,7 mg', price: 1399 },
      { dose: '2,4 mg', price: 1699 }
    ])}. O medicamento também pode ser adquirido na farmácia de preferência do paciente.`
  }
}
